import { useState, useEffect } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Package, 
  Calendar, 
  MapPin, 
  TrendingUp, 
  Loader2,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// Chart colors
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];

export default function MyPackages() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('month');

  // Helper function to format date as YYYY-MM-DD in local timezone (not UTC)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (user?.id) {
      fetchRegions();
      // Set default to this month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(formatLocalDate(startOfMonth));
      setEndDate(formatLocalDate(now));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && startDate && endDate) {
      fetchPackages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, startDate, endDate, regionFilter]);

  const fetchRegions = async () => {
    try {
      const response = await api.get(`/loonbrief/user-packages-regions/${user.id}`);
      if (response.data.success) {
        setRegions(response.data.data.regions || []);
        setDateRange(response.data.data.date_range);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (regionFilter) params.append('region', regionFilter);
      
      const response = await api.get(`/loonbrief/user-packages/${user.id}?${params.toString()}`);
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Paket verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const presets = [
    { key: 'today', label: 'Bugün', getValue: () => {
      const today = formatLocalDate(new Date());
      return { start: today, end: today };
    }},
    { key: 'yesterday', label: 'Dün', getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const formatted = formatLocalDate(yesterday);
      return { start: formatted, end: formatted };
    }},
    { key: 'week', label: 'Bu Hafta', getValue: () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      return { start: formatLocalDate(startOfWeek), end: formatLocalDate(now) };
    }},
    { key: 'month', label: 'Bu Ay', getValue: () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: formatLocalDate(startOfMonth), end: formatLocalDate(now) };
    }},
    { key: 'lastMonth', label: 'Geçen Ay', getValue: () => {
      const now = new Date();
      const startOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLast = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: formatLocalDate(startOfLast), end: formatLocalDate(endOfLast) };
    }},
    { key: 'year', label: 'Bu Yıl', getValue: () => {
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return { start: formatLocalDate(startOfYear), end: formatLocalDate(now) };
    }},
  ];

  const handlePresetClick = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset.key);
  };

  return (
    <div className="space-y-6" data-testid="my-packages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.myPackages') || 'Paketlerim'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('employeeProfile.packagesDesc') || 'Paket dağıtım verileriniz'}
          </p>
        </div>
      </div>

      {/* Mart 01, 2026 — Admin bu kullanıcı için paket gösterimini kapatmış */}
      {packages?.hidden_by_admin && (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="py-4 flex items-start gap-3">
            <Package className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">Paket eşleştirmesi kapatıldı</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Yöneticiniz, ad/soyad çakışması nedeniyle bu hesap için otomatik paket eşleştirmesini
                kapatmıştır. Doğru verileri görmek için admininize başvurun.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Quick Selection Buttons */}
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <Button
                  key={preset.key}
                  variant={selectedPreset === preset.key ? "default" : "outline"}
                  size="sm"
                  className="px-3 py-1.5 text-xs h-8"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden sm:block" />

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setSelectedPreset('');
                }}
                className="h-8 w-32 text-xs"
              />
              <span className="text-slate-400 text-xs">-</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setSelectedPreset('');
                }}
                className="h-8 w-32 text-xs"
              />
            </div>

            {/* Region Filter */}
            <Select value={regionFilter || "all"} onValueChange={(v) => setRegionFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-32 text-xs">
                <SelectValue placeholder="Tüm Bölgeler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Bölgeler</SelectItem>
                {regions.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Refresh Button */}
            <Button onClick={fetchPackages} disabled={loading} size="sm" className="h-8">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : !packages ? (
        <Card className="p-12 text-center">
          <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
            {t('employeeProfile.noPackages') || 'Paket verisi bulunamadı'}
          </h3>
          <p className="text-sm text-slate-400">
            {t('employeeProfile.noPackagesDesc') || 'Paket dağıtım verileri yüklendiğinde burada görüntülenecektir'}
          </p>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-blue-600 truncate">
                      {packages.summary?.total_quantity?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Toplam Paket</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-emerald-600">
                      {packages.summary?.total_days || 0}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Çalışılan Gün</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-amber-600">
                      {packages.summary?.avg_daily || 0}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Günlük Ort.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-purple-600">
                      {packages.summary?.regions?.length || 0}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Bölge</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          {packages.daily_breakdown?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Packages Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    Günlük Paket Dağılımı
                  </CardTitle>
                  <CardDescription>Son 30 günün paket sayısı</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]" style={{ minHeight: 300, minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={packages.daily_breakdown.slice(0, 30).reverse().map(d => ({
                          date: new Date(d.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
                          paket: d.total_quantity
                        }))}
                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11 }} 
                          angle={-45} 
                          textAnchor="end" 
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                          formatter={(value) => [`${value} adet`, 'Paket']}
                        />
                        <Bar dataKey="paket" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Region Distribution Pie Chart */}
              {packages.regions_breakdown?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-purple-600" />
                      Bölge Dağılımı
                    </CardTitle>
                    <CardDescription>Paketlerin bölgelere göre dağılımı</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]" style={{ minHeight: 300, minWidth: 0 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={packages.regions_breakdown.map((r) => ({
                              name: r.region,
                              value: r.total_quantity
                            }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            innerRadius={50}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={true}
                          >
                            {packages.regions_breakdown.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                            }}
                            formatter={(value, name, props) => [
                              `${value.toLocaleString()} paket`,
                              props.payload.name
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Regions Breakdown */}
          {packages.regions_breakdown?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                  Bölgelere Göre Dağılım
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {packages.regions_breakdown.map((r, idx) => {
                    const maxQty = Math.max(...packages.regions_breakdown.map(x => x.total_quantity));
                    const percentage = maxQty > 0 ? (r.total_quantity / maxQty) * 100 : 0;
                    return (
                      <div key={idx} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-slate-900 dark:text-slate-100">{r.region}</span>
                          <span className="text-slate-500 dark:text-slate-400">
                            {r.total_quantity.toLocaleString()} paket ({r.total_days} gün)
                          </span>
                        </div>
                        <Progress value={percentage} className="h-3" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily Breakdown Table */}
          {packages.daily_breakdown?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Günlük Detay
                </CardTitle>
                <CardDescription>
                  Son {Math.min(30, packages.daily_breakdown.length)} gün
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 dark:bg-slate-800">
                        <th className="text-left p-3 font-medium text-slate-900 dark:text-slate-100">Tarih</th>
                        <th className="text-left p-3 font-medium text-slate-900 dark:text-slate-100">Bölgeler</th>
                        <th className="text-right p-3 font-medium text-slate-900 dark:text-slate-100">Adet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.daily_breakdown.slice(0, 30).map((d, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="p-3 text-slate-900 dark:text-slate-100">
                            {new Date(d.date).toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(d.regions || {}).map(([region, qty]) => (
                                <Badge key={region} variant="outline" className="text-xs">
                                  {region}: {qty}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="p-3 text-right font-medium text-blue-600">
                            {d.total_quantity.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
